import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    model,
    output,
} from '@angular/core';
import {NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {
    caseInsensitiveComparator,
    composeComparators,
    fromControl,
    includesIgnoreCase,
    keyComparator,
    TranslatePipe,
} from '@brunata-angular/util';
import {InputTextGroupComponent, SortDirection} from '@brunata-angular/ui';
import {ExportNoteComponent} from '../../shared/ui/export-note/export-note.component';
import {SectionToggleComponent} from '../../shared/ui/section-toggle/section-toggle.component';
import {
    SelectionTableColumn,
    SelectionTableComponent,
} from '../../shared/ui/selection-table/selection-table.component';
import {SelectionTableCellDirective} from '../../shared/ui/selection-table/selection-table-templates.directive';
import {MeterChannelRow, MeterSuperTypeOption} from './meter-scope.model';

const byChannel = (
    direction: SortDirection,
): ((a: MeterChannelRow, b: MeterChannelRow) => number) =>
    caseInsensitiveComparator<MeterChannelRow>(channel => channel.accessibleName, direction);

type ChannelsView = 'failed' | 'loading' | 'noSuperTypes' | 'none' | 'table';

@Component({
    selector: 'app-channels-list',
    standalone: true,
    imports: [
        TranslatePipe,
        ReactiveFormsModule,
        InputTextGroupComponent,
        ExportNoteComponent,
        SectionToggleComponent,
        SelectionTableComponent,
        SelectionTableCellDirective,
    ],
    template: `
        <app-section-toggle [(expanded)]="expanded" controls="channels-panel">
            <span class="fw-bold">
                {{ 'customizedexports_export_channels_title' | translate: 'online' : 'export' }}
                <span class="text-danger">*</span>
            </span>
            <span
                class="channels-meta d-flex flex-wrap align-items-baseline column-gap-4 fw-normal text-muted small">
                <span class="channels-hint">
                    {{
                        'customizedexports_export_channels_supertypes_hint'
                            | translate: 'online' : 'export'
                    }}
                </span>
                @if (view() === 'table') {
                    <span class="ms-auto me-2 text-end">
                        {{
                            'customizedexports_export_channels_selected_of_total'
                                | translate
                                    : 'online'
                                    : 'export'
                                    : [selectedCount().toString(), channels().length.toString()]
                        }}
                    </span>
                }
            </span>
        </app-section-toggle>

        @if (expanded()) {
            <div id="channels-panel">
                @if (view() === 'failed') {
                    <app-export-note
                        class="d-block mx-3 mb-3"
                        icon="bi-exclamation-triangle"
                        tone="muted">
                        {{
                            'customizedexports_export_channels_load_error'
                                | translate: 'online' : 'export'
                        }}
                    </app-export-note>
                } @else if (view() === 'loading') {
                    <p class="text-muted small mx-3 mb-3">
                        {{
                            'customizedexports_export_channels_loading'
                                | translate: 'online' : 'export'
                        }}
                    </p>
                } @else if (view() === 'noSuperTypes') {
                    <app-export-note class="d-block mx-3 mb-3" icon="bi-info-circle-fill">
                        {{
                            'customizedexports_export_channels_empty'
                                | translate: 'online' : 'export'
                        }}
                    </app-export-note>
                } @else if (view() === 'none') {
                    <app-export-note class="d-block mx-3 mb-3" icon="bi-info-circle-fill">
                        {{
                            'customizedexports_export_channels_none'
                                | translate: 'online' : 'export'
                        }}
                    </app-export-note>
                } @else {
                    <form
                        class="d-flex align-items-center flex-wrap gap-3 px-3 pb-3"
                        [formGroup]="filterForm">
                        <app-input-text-group
                            class="search-field"
                            formControlName="query"
                            [placeholder]="
                                'customizedexports_export_channels_search'
                                    | translate: 'online' : 'export'
                            " />
                        @if (canNarrowToPrimary()) {
                            <button
                                type="button"
                                class="btn btn-sm btn-outline-primary ms-auto"
                                (click)="applyPrimary()">
                                {{
                                    'customizedexports_export_channels_primary_only'
                                        | translate: 'online' : 'export'
                                }}
                            </button>
                        }
                    </form>

                    <app-selection-table
                        [rows]="filteredChannels()"
                        [columns]="columns()"
                        sortedBy="superType"
                        [rowLabel]="accessibleName"
                        [selectAllLabel]="
                            'customizedexports_export_channels_all' | translate: 'online' : 'export'
                        "
                        [emptyText]="
                            'customizedexports_export_channels_no_match'
                                | translate: 'online' : 'export'
                        "
                        (rowToggled)="channelToggled.emit($event)"
                        (rowsSelected)="channelsSelected.emit($event)"
                        (rowsDeselected)="channelsDeselected.emit($event)">
                        <ng-template
                            appSelectionTableCell="superType"
                            [appSelectionTableCellOf]="filteredChannels()"
                            let-channel>
                            {{ channel.superTypeNameKey | translate: 'online' : 'common' }}
                        </ng-template>
                        <ng-template
                            appSelectionTableCell="channel"
                            [appSelectionTableCellOf]="filteredChannels()"
                            let-channel>
                            <span class="fw-bold">{{ channel.label.code }}</span>
                            @if (channel.label.name !== null) {
                                <span class="text-muted"> - {{ channel.label.name }}</span>
                            }
                        </ng-template>
                        <ng-template
                            appSelectionTableCell="unit"
                            [appSelectionTableCellOf]="filteredChannels()"
                            let-channel>
                            @if (channel.unitKey !== null) {
                                {{ channel.unitKey | translate: 'online' : 'common' }}
                            }
                        </ng-template>
                        <ng-template
                            appSelectionTableCell="models"
                            [appSelectionTableCellOf]="filteredChannels()"
                            let-channel>
                            {{
                                'customizedexports_export_channels_models_of_total'
                                    | translate
                                        : 'online'
                                        : 'export'
                                        : [
                                              channel.offeringModelCount.toString(),
                                              channel.modelCount.toString(),
                                          ]
                            }}
                        </ng-template>
                        <ng-template
                            appSelectionTableCell="primary"
                            [appSelectionTableCellOf]="filteredChannels()"
                            let-channel>
                            @if (marksPrimary() && channel.primaryModelCount > 0) {
                                @if (channel.primaryModelCount === channel.offeringModelCount) {
                                    <span class="badge bg-primary text-uppercase">
                                        {{
                                            'customizedexports_export_channels_primary_badge'
                                                | translate: 'online' : 'export'
                                        }}
                                    </span>
                                } @else {
                                    <span
                                        class="badge bg-primary text-uppercase"
                                        [title]="
                                            'customizedexports_export_channels_primary_badge_partial_title'
                                                | translate
                                                    : 'online'
                                                    : 'export'
                                                    : [
                                                          channel.primaryModelCount.toString(),
                                                          channel.offeringModelCount.toString(),
                                                      ]
                                        ">
                                        {{
                                            'customizedexports_export_channels_primary_badge_partial'
                                                | translate
                                                    : 'online'
                                                    : 'export'
                                                    : [
                                                          channel.primaryModelCount.toString(),
                                                          channel.offeringModelCount.toString(),
                                                      ]
                                        }}
                                    </span>
                                }
                            }
                        </ng-template>
                    </app-selection-table>
                }
            </div>
        }
    `,
    styles: `
        .channels-meta {
            flex: 1 1 20rem;
        }

        .channels-hint {
            flex: 1 1 12rem;
        }

        .search-field {
            display: block;
            width: 16rem;
            max-width: 100%;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelsListComponent {
    private readonly formBuilder = inject(NonNullableFormBuilder);

    readonly channels = input.required<ReadonlyArray<MeterChannelRow>>();
    readonly superTypes = input.required<ReadonlyArray<MeterSuperTypeOption>>();
    readonly loading = input.required<boolean>();
    readonly failed = input.required<boolean>();
    readonly offersNoChannels = input.required<boolean>();
    readonly noSuperTypesSelected = input.required<boolean>();
    readonly expanded = model(true);

    readonly channelToggled = output<string>();
    readonly channelsSelected = output<string[]>();
    readonly channelsDeselected = output<string[]>();

    protected readonly filterForm = this.formBuilder.group({query: this.formBuilder.control('')});

    private readonly filters = toSignal(fromControl(this.filterForm), {
        initialValue: this.filterForm.getRawValue(),
    });

    protected readonly view = computed((): ChannelsView => {
        if (this.failed()) {
            return 'failed';
        }
        if (this.loading()) {
            return 'loading';
        }
        if (this.noSuperTypesSelected()) {
            return 'noSuperTypes';
        }
        return this.offersNoChannels() ? 'none' : 'table';
    });

    // A channel is primary per model-and-channel pair, so the contrast can sit inside one row
    // (primary on 4 of 5 models) as well as between rows.
    protected readonly marksPrimary = computed(() =>
        this.channels().some(channel => channel.primaryModelCount !== channel.offeringModelCount),
    );

    protected readonly selectedCount = computed(
        () => this.channels().filter(channel => channel.selected).length,
    );

    protected readonly columns = computed((): SelectionTableColumn<MeterChannelRow>[] => [
        {
            key: 'superType',
            labelKey: 'customizedexports_export_step_models_col_super_type',
            hidden: this.superTypes().length <= 1,
            compare: direction =>
                composeComparators<MeterChannelRow>([
                    keyComparator<MeterChannelRow, number>(
                        channel => Number(channel.meterSuperTypeNo),
                        direction,
                    ),
                    byChannel(direction),
                ]),
        },
        {
            key: 'channel',
            labelKey: 'customizedexports_export_channels_col_channel',
            text: channel => channel.accessibleName,
            truncate: true,
            compare: byChannel,
        },
        {
            key: 'unit',
            labelKey: 'customizedexports_export_channels_col_unit',
            cellClass: 'text-muted',
            compare: direction =>
                composeComparators<MeterChannelRow>([
                    caseInsensitiveComparator<MeterChannelRow>(
                        channel => channel.unitKey ?? '',
                        direction,
                    ),
                    byChannel(direction),
                ]),
        },
        {
            key: 'models',
            labelKey: 'customizedexports_export_channels_col_models',
            cellClass: 'text-muted',
            compare: direction =>
                keyComparator<MeterChannelRow, number>(
                    channel => channel.offeringModelCount,
                    direction,
                ),
        },
        {
            key: 'primary',
            alignEnd: true,
        },
    ]);

    // Primary only would just repeat All unless some channel is primary on no model at all.
    protected readonly canNarrowToPrimary = computed(() =>
        this.channels().some(channel => channel.primaryModelCount === 0),
    );

    protected readonly filteredChannels = computed(() => {
        const query = this.filters().query ?? '';
        return this.channels().filter(
            channel =>
                includesIgnoreCase(channel.label.code, query) ||
                includesIgnoreCase(channel.label.name, query),
        );
    });

    protected readonly accessibleName = (channel: MeterChannelRow): string =>
        channel.accessibleName;

    protected applyPrimary(): void {
        const filtered = this.filteredChannels();
        this.channelsSelected.emit(
            filtered.filter(channel => channel.primaryModelCount > 0).map(channel => channel.id),
        );
        this.channelsDeselected.emit(
            filtered.filter(channel => channel.primaryModelCount === 0).map(channel => channel.id),
        );
    }
}
